<script setup lang="ts">
  import PrinterIcon from '@/shared/assets/icons/PrinterIcon.svg';
  import { IconImage, SimpleLoading } from '@/shared/ui';
  import { isAxiosError } from 'axios';
  import { isNetworkError, handleApiError } from '@/shared/lib/utils';
  import { ref, onMounted, computed } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import type { MasterListDocument } from '@/entities/Document';
  import { documentApi } from '@/entities/Document';
  import { useBranchLogo } from '@/entities/Branch';
  import { getExternalRevisionDisplay } from '../lib/external-revision-display';
  import { useMasterListPrint } from '../model/useMasterListPrint';

  const route = useRoute();
  const router = useRouter();
  const { printMasterList } = useMasterListPrint();
  const { currentLogoUrl, handleLogoError, resolvePrintableLogoSrc } = useBranchLogo();
  const MASTER_LIST_CODE = 'DA05.1';

  const documentInfo = ref({
    code: MASTER_LIST_CODE,
    revision: getCurrentDate(),
  });

  const documentList = ref<MasterListDocument[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  function getCurrentDate(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const isExternalList = computed(() => route.query.type === 'externo');
  const revisionColumnLabel = computed(() =>
    isExternalList.value ? 'Revisão Externa:' : 'Revisão:'
  );

  const title = computed(() => {
    const type = route.query.type as string;
    if (type === 'interno') {
      return 'LISTA MESTRA DE DOCUMENTOS INTERNOS';
    } else if (type === 'externo') {
      return 'LISTA MESTRA DE DOCUMENTOS EXTERNOS';
    }
    return 'LISTA MESTRA DE DOCUMENTOS';
  });

  async function fetchMasterList() {
    try {
      loading.value = true;
      error.value = null;

      const type = route.query.type as string;
      const status = route.query.status as string;
      const prefix = route.query.prefix as string;
      const format = route.query.format as string;
      const startDate = route.query.startDate as string;
      const endDate = route.query.endDate as string;
      const generate = route.query.generate as string;

      if (!type || !status || !format) {
        error.value = 'Parâmetros obrigatórios não informados. Por favor, use o modal de filtros.';
        return;
      }

      const params: {
        type: string;
        status: string;
        prefix?: string;
        format: string;
        startDate?: string;
        endDate?: string;
        view?: string;
      } = {
        type,
        status,
        format,
      };

      if (prefix) {
        params.prefix = prefix;
      }

      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      if (generate !== 'true') {
        params.view = 'true';
      }

      const data = await documentApi.getMasterList(params);

      if (data.success) {
        documentList.value = data.data.data;

        if (generate === 'true') {
          const newQuery = { ...route.query };
          delete newQuery.generate;
          router.replace({ query: newQuery });
        }
      } else {
        throw new Error(data.message || 'Erro ao carregar dados');
      }
    } catch (err) {
      if (isAxiosError(err) && isNetworkError(err)) {
        error.value =
          'Não foi possível gerar a Lista Mestra. Verifique sua conexão e tente novamente.';
      } else {
        error.value = handleApiError(err, 'Erro desconhecido').message;
      }
    } finally {
      loading.value = false;
    }
  }

  const handlePrint = async () => {
    await printMasterList({
      code: documentInfo.value.code,
      documents: documentList.value,
      isExternalList: isExternalList.value,
      logoSrc: resolvePrintableLogoSrc(),
      revision: documentInfo.value.revision,
      revisionColumnLabel: revisionColumnLabel.value,
      title: title.value,
    });
  };

  onMounted(() => {
    fetchMasterList();
  });
</script>

<template>
  <div
    id="lista-mestra-content"
    class="bg-white-01 p-6"
  >
    <table class="w-full border-collapse border border-gray-400 mb-6">
      <thead>
        <tr>
          <td
            rowspan="2"
            class="border border-gray-400 py-4 w-60 text-center"
          >
            <div class="flex flex-col items-center">
              <img
                :src="currentLogoUrl"
                alt="Logo"
                class="max-w-[170px] max-h-[84px]"
                @error="handleLogoError"
              />
            </div>
          </td>
          <td class="border border-gray-400 p-4 text-center">
            <div class="text-lg font-semibold text-gray-800">
              DADOS
            </div>
          </td>
          <td class="border border-gray-400 p-2 text-center w-40">
            <div class="text-sm font-semibold">
              {{ documentInfo.code }}
            </div>
          </td>
          <td class="border border-gray-400 p-2 text-center w-40">
            <div class="text-sm font-semibold">
              {{ documentInfo.revision }}
            </div>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td
            colspan="3"
            class="border border-gray-400 p-4 text-center"
          >
            <div class="text-lg font-semibold text-gray-800">
              {{ title }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <button
      class="bg-[var(--theme-secondary)] ml-auto cursor-pointer text-white px-2 py-2 rounded flex items-center gap-2 transition-colors hover:opacity-90 print-hidden"
      @click="handlePrint"
    >
      <IconImage
        name="PrinterIcon"
        :src="PrinterIcon"
        alt="Imprimir"
      />
    </button>

    <div class="border-t-1 border-light-gray-xx my-6" />
    <div class="mx-6">
      <div
        v-if="loading"
        class="w-full flex justify-center"
      >
        <SimpleLoading />
      </div>

      <div
        v-else-if="error"
        class="text-center py-8"
      >
        <div class="text-red-500 text-lg">
          {{ error }}
        </div>
        <button
          class="mt-4 bg-[var(--theme-secondary)] text-white px-4 py-2 rounded hover:opacity-90 print-hidden"
          @click="fetchMasterList"
        >
          Tentar novamente
        </button>
      </div>

      <div v-else>
        <div class="grid grid-cols-12 gap-4 mb-6">
          <div class="col-span-2 font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">
            Prefixo:
          </div>
          <div class="col-span-2 font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">
            Nome do Documento:
          </div>
          <div class="col-span-2 font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">
            {{ revisionColumnLabel }}
          </div>
          <div class="col-span-2 font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">
            Vigência:
          </div>
          <div class="col-span-2 font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">
            Situação:
          </div>
          <div class="col-span-2 font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">
            Formato:
          </div>
        </div>

        <div class="space-y-4">
          <div
            v-for="(document, index) in documentList"
            :key="index"
            class="grid grid-cols-12 gap-4"
          >
            <div
              class="col-span-2 rounded-2xl border flex items-center p-5 border-border-color bg-gray-02/20"
            >
              <span class="text-sm">{{ document.prefix }}</span>
            </div>
            <div
              class="col-span-2 rounded-2xl border flex items-center p-5 border-border-color bg-gray-02/20"
            >
              <span class="text-sm">{{ document.documentName ?? '-' }}</span>
            </div>
            <div
              class="col-span-2 rounded-2xl border flex items-center p-5 border-border-color bg-gray-02/20"
            >
              <span class="text-sm">{{
                isExternalList
                  ? getExternalRevisionDisplay(document.externalRevision)
                  : document.revision
              }}</span>
            </div>
            <div
              class="col-span-2 rounded-2xl border flex items-center p-5 border-border-color bg-gray-02/20"
            >
              <span class="text-sm">{{ document.validityDate ?? '-' }}</span>
            </div>
            <div
              class="col-span-2 rounded-2xl border flex items-center p-5 border-border-color bg-gray-02/20"
            >
              <span class="text-sm">{{ document.status }}</span>
            </div>
            <div
              class="col-span-2 rounded-2xl border flex items-center p-5 border-border-color bg-gray-02/20"
            >
              <span class="text-sm">{{ document.format }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="documentList.length === 0"
          class="text-center py-8"
        >
          <div class="text-gray-500 text-lg">
            Nenhum documento encontrado
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @media print {
    .print-hidden {
      display: none !important;
    }

    body {
      margin: 0;
      padding: 20px;
    }
  }
</style>
